import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress, Stack, Chip,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import {
    extractPayloadFieldsManual,
    formFieldsToManualMap,
    manualMapToFormFields,
    resolvePayloadSchemaFormat,
    buildPayloadSchemaForSave,
} from "../../utils/payloadSchemaManual";
import { loyaltyUserMessage } from "../../utils/loyaltyApiMessage";
import PayloadFieldTypeSelect from "./PayloadFieldTypeSelect";

const ORIGINS = ["INTERNAL", "EXTERNAL"];

const EditTransactionTypeDialog = ({ open, transactionType, onClose, onSaved }) => {
    const [form, setForm] = useState({ key: "", origin: "INTERNAL", name: "", description: "", payload_fields: [], active: true, brand: "" });
    const [schemaFormat, setSchemaFormat] = useState(null);
    const [enrichedFields, setEnrichedFields] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !transactionType?.id) return;

        let cancelled = false;
        setLoadingDetail(true);
        setError(null);

        loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types/${transactionType.id}`, {
            headers: { "X-Brand": transactionType.brand },
        })
            .then(async (res) => {
                if (!res.ok) {
                    const e = await res.json().catch(() => null);
                    throw new Error(loyaltyUserMessage(e?.detail, `Erreur HTTP ${res.status}`));
                }
                return res.json();
            })
            .then((detail) => {
                if (cancelled) return;
                const format = resolvePayloadSchemaFormat(detail);
                const manual = extractPayloadFieldsManual(detail);
                setSchemaFormat(format);
                setEnrichedFields(Array.isArray(detail.payload_fields) ? detail.payload_fields : []);
                setForm({
                    key: detail.key || "",
                    origin: detail.origin || "INTERNAL",
                    name: detail.name || "",
                    description: detail.description || "",
                    payload_fields: manualMapToFormFields(manual),
                    active: detail.active ?? true,
                    brand: detail.brand || "",
                });
            })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoadingDetail(false); });

        return () => { cancelled = true; };
    }, [open, transactionType?.id, transactionType?.brand]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.origin.trim() && form.name.trim();

    const handleAddField = () => {
        setForm((p) => ({
            ...p,
            payload_fields: [...p.payload_fields, { id: Date.now(), key: "", type: "string", description: "" }],
        }));
    };

    const handleUpdateField = (id, field, value) => {
        setForm((p) => ({
            ...p,
            payload_fields: p.payload_fields.map((f) => (f.id === id ? { ...f, [field]: value } : f)),
        }));
    };

    const handleRemoveField = (id) => {
        setForm((p) => ({
            ...p,
            payload_fields: p.payload_fields.filter((f) => f.id !== id),
        }));
    };

    const handleSubmit = async () => {
        if (!isValid || !transactionType) return;
        setSubmitting(true);
        setError(null);
        try {
            const manualMap = formFieldsToManualMap(form.payload_fields);
            const body = {
                origin: form.origin.trim(),
                name: form.name.trim(),
                description: form.description.trim() || null,
                payload_schema: buildPayloadSchemaForSave(schemaFormat, manualMap),
                active: form.active,
                brand: form.brand || null,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types/${transactionType.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": form.brand || transactionType.brand,
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(loyaltyUserMessage(e?.detail, `Erreur HTTP ${res.status}`));
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!transactionType) return null;
    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const enrichedByName = new Map(enrichedFields.map((f) => [f.name, f]));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.transactionTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier le Type de Transaction
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{transactionType.key}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {loadingDetail ? (
                    <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth required select label="Origine" value={form.origin}
                                onChange={handle("origin")} size="small" sx={fieldSx}>
                                {ORIGINS.map((o) => (
                                    <MenuItem key={o} value={o}>
                                        {o === "INTERNAL" ? "Internal" : "External"}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth required label="Nom affiché" value={form.name}
                                onChange={handle("name")} size="small" sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description" value={form.description}
                                onChange={handle("description")} size="small" multiline rows={2} sx={fieldSx} />
                        </Grid>

                        <Grid item xs={12}>
                            <Box mt={1} p={2} sx={{ borderRadius: 2, bgcolor: "#f8f9fa", border: "1px dashed #ced4da" }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                                    <Typography variant="subtitle2" fontWeight={700} color="#495057">
                                        Champs payload (configuration manuelle)
                                    </Typography>
                                    {schemaFormat && (
                                        <Chip size="small" label={`format: ${schemaFormat}`}
                                            sx={{ fontSize: 10, fontWeight: 600 }} />
                                    )}
                                </Box>
                                {schemaFormat === "json_schema" && (
                                    <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>
                                        Types lus depuis le schéma de l’événement (email, téléphone, date…).
                                        Modifier un type ici met à jour le schéma envoyé au backend.
                                    </Alert>
                                )}
                                {form.payload_fields.length === 0 && (
                                    <Typography fontSize={12} color="text.secondary" mb={1}>
                                        {form.key === "sale"
                                            ? "Aucun champ configuré — normal avant la première transaction sale. Les champs apparaîtront après ingestion."
                                            : "Aucun champ personnalisé. Cliquez sur « Ajouter » pour en définir."}
                                    </Typography>
                                )}
                                <Box display="flex" justifyContent="flex-end" mb={1.5}>
                                    <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddField}
                                        sx={{ color: SECTION_COLORS.transactionTypes.main, textTransform: "none", fontWeight: 600 }}>
                                        Ajouter un champ
                                    </Button>
                                </Box>
                                {form.payload_fields.length > 0 && (
                                    <Stack spacing={1.5}>
                                        {form.payload_fields.map((f) => {
                                            const enriched = enrichedByName.get(f.key);
                                            return (
                                                <Box key={f.id}>
                                                    <Box display="flex" gap={1} alignItems="flex-start">
                                                        <TextField size="small" label="Nom du champ" value={f.key}
                                                            onChange={(e) => handleUpdateField(f.id, "key", e.target.value)}
                                                            placeholder="ex: orderTotal" sx={{ flex: 1, ...fieldSx }} />
                                                        <PayloadFieldTypeSelect
                                                            value={f.type}
                                                            onChange={(e) => handleUpdateField(f.id, "type", e.target.value)}
                                                            sx={fieldSx}
                                                        />
                                                        <TextField size="small" label="Description" value={f.description}
                                                            onChange={(e) => handleUpdateField(f.id, "description", e.target.value)}
                                                            placeholder="Facultatif" sx={{ flex: 1.5, ...fieldSx }} />
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveField(f.id)} sx={{ mt: 0.5 }}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                    {enriched?.role && (
                                                        <Typography fontSize={10} color="text.secondary" mt={0.5} ml={0.5}>
                                                            Rôle API : {enriched.role}
                                                            {enriched.conditionField ? ` · condition ${enriched.conditionField}` : ""}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                                sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                                <Box>
                                    <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                                    <Typography fontSize={11} color="text.secondary">Type disponible pour les règles</Typography>
                                </Box>
                                <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.transactionTypes.main },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.transactionTypes.main },
                                    }} />
                            </Box>
                        </Grid>
                    </Grid>
                )}
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting || loadingDetail}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.transactionTypes.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTransactionTypeDialog;
